import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useScrollTop } from "hooks/useScrollTop";
import { useForm } from "hooks/useForm";

import { animations } from "helpers/animations";
import {
  FormControl,
  Grid,
  TextField,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import { AutocompleteComponent } from "components/Autocomplete/AutocompleteComponent";
import { typesUnitsOpt } from "helpers/dictionary";
import RegularButton from "components/CustomButtons/Button";
import { useSearch } from "hooks/useSearch";
import { createGasMovement } from "redux/actions/gasMovementsRequests/gasMovementsRequestActions";
import { useCheckInventoryStatus } from "hooks/fetch-hooks/useCheckInventoryStatus";
import { useFetchLocal } from "hooks/fetch-hooks/useFetchLocal";
import { fetchNotes } from "./fetchData";
import { resetOnClick } from "helpers/resetOnClick";
import { orderArrayV2 } from "helpers/orderArray";
import { initialValues, isTransferMovement } from "./initialValues";
import { filterMovements } from "../filterMovements";
import { useTransferWarehouses } from "./useTransferWarehouses";

export const FormGasMovements = ({ onClose, isEdit = false, data = {} }) => {
  const TO_SKIP = "AJUSTE CLIENTE";
  const CREDIT = "CREDITO";
  const CASH = "CONTADO";
  const PORTATIL = typesUnitsOpt[1].value;
  const ESTACION = typesUnitsOpt[2].value;
  const AUTOTANQUE = typesUnitsOpt[3].value;

  const dispatch = useDispatch();
  const { setSnackbar } = useSearch();
  const { station } = useSelector((state) => state.gasMovementsRequests);
  const { movementsArr } = useSelector(({ gasMovements }) => gasMovements);
  const { unitsMap } = useSelector(({ unitsReducer }) => unitsReducer);
  const { id, username, user: userLog } = useSelector(({ login }) => login);
  const { isLoadingComp } = useSelector(({ search }) => search);
  const { stationsList } = useSelector(({ companies }) => companies);

  const [
    [responseNotes, loadingNotes, successNotes],
    requestNotes,
  ] = useFetchLocal(fetchNotes);
  const ref = useScrollTop();
  //mete todo lo de fechas en useCallback
  const dates = useCallback(() => {
    const currentDate = new Date();
    const maxDate = new Date();
    maxDate.setDate(currentDate.getDate());

    const maxDateString = maxDate.toISOString().split("T")[0];
    const dateTemp = new Date(2025, 0, 1);
    const dateTempString = dateTemp.toISOString().split("T")[0];

    return { maxDateString, dateTempString };
  }, []);

  const {
    inputValues,
    handleInputOnChange,
    setValues,
    fieldErrors,
    validate,
  } = useForm(initialValues.inputValues, initialValues.required);

  const {
    idStation,
    idUnit,
    unitsByType,
    dateMovement,
    idGasMovement,
    idNote,
    kilograms,
    liters,
    comment,
    type,
    notesFoliosOpt,
    movGasOpt,
  } = inputValues;

  const isTransfer = isTransferMovement(idGasMovement);

  const { warehouseOptions } = useTransferWarehouses({
    isTransfer,
    stationDestino: inputValues.idStationDestino,
    setValues,
  });

  const { isSuccess, errorInv, message: messageInv } = useCheckInventoryStatus(idStation?.id, dateMovement);

  const handleChange = (v) => {
    setValues({
      idStation: v,
      type: null,
      idUnit: null,
      idGasMovement: null,
      liters: 0,
      idNote: null,
      notesFoliosOpt: [],
    });
  };

  const handleChangeUnitType = useCallback(
    ({ type }) => {
      if (type) {
        const filter = unitsMap.filter(
          (unit) =>
            unit.type === type.value && unit.idStation === idStation?.id,
        );
        setValues({ unitsByType: filter });
      }

      setValues({
        type: type,
        idUnit: null,
        idGasMovement: null,
        liters: 0,
        idNote: null,
        notesFoliosOpt: [],
      });
    },
    [unitsMap, idStation],
  );

  const handleChangeUnit = useCallback(
    ({ idUnit }) => {
      if (idUnit?.isMaintenance) {
        dispatch(setSnackbar(`UNIDAD ${idUnit?.number} EN MANTENIMIENTO, CONTACTA A SOPORTE EN CASO DE SER NECESARIO`))
        setValues({
          idUnit: null,
          unit: null,
        });
        return
      }
      setValues({
        idUnit: idUnit,
        idGasMovement: null,
        liters: 0,
        idNote: null,
        notesFoliosOpt: [],
      });

      if (idUnit) {
        requestNotes(idUnit?.id);
        const movementsFiltered = filterMovements(idUnit?.type, movementsArr);
        setValues({
          movGasOpt: movementsFiltered,
        });
      }
    },
    [unitsMap, idStation],
  );

  const handleChangeMov = useCallback(
    ({ idGasMovement }) => {
      setValues({
        movement: idGasMovement?.movement || "",
        idGasMovement: idGasMovement || null,
        movementType: idGasMovement?.movementType || "",
      });
    },
    [unitsMap, idStation],
  );

  const unitsByTypeMemo = useMemo(() => {
    return orderArrayV2(unitsByType);
  }, [unitsByType]);

  const typesUnitsOptMemo = useMemo(() => {
    return typesUnitsOpt.filter((a) => a.value !== AUTOTANQUE);
  }, [typesUnitsOpt]);

  const handleSubmit = useCallback(() => {
    let fValidate = idGasMovement?.withoutNote === 1 ? ["idNote"] : [];
    validate(fValidate, () => {
      const {
        notesFoliosOpt: _notesFoliosOpt,
        movGasOpt: _movGasOpt,
        unitsByType: _unitsByType,
        ...rest
      } = inputValues;
      const form = {
        ...rest,
        type: inputValues.type.value,
        idUnit: inputValues.idUnit.id,
        idUSer: id,
        idStation: idStation?.id,
        idGasMovement: inputValues.idGasMovement?.id,
        idNote: inputValues.idNote?.id,
        kilograms: parseInt(kilograms),
        liters: parseFloat(liters),
        unit: inputValues.idUnit.number,
        ...(isTransfer && {
          idStationDestino: inputValues.idStationDestino?.id,
          idStorageDestino: inputValues.idStorageDestino?.id,
          stationDestino: inputValues.idStationDestino?.label || inputValues.idStationDestino?.name || "",
          storageDestino: inputValues.idStorageDestino?.label || inputValues.idStorageDestino?.number || "",
        }),
      };
      dispatch(createGasMovement(form, onClose));
    });
  }, [inputValues]);

  useEffect(() => {
    if (isEdit) {
      setValues({
        ...data,
        type: typesUnitsOpt.find((t) => t.value === data.type) || null,
        idUnit: unitsMap.find((u) => u.id === data.idUnit) || null,
        idGasMovement:
          movGasOpt.find((m) => m.id === data.idGasMovement) || null,
        idNote: notesFoliosOpt.find((n) => n.id === data.idNote) || null,
      });
    }
    setValues({
      idUser: id,
      user: username,
      employee: userLog,
      idStation: station,
    });
  }, []);

  useEffect(() => {
    successNotes &&
      setValues({
        notesFoliosOpt: responseNotes,
        idNote: responseNotes?.length === 1 ? responseNotes[0] : null,
      });
  }, [successNotes, responseNotes]);

  useEffect(() => {
    const isPortatil = type?.value === PORTATIL;
    const density = 0.54;
    const maxQuantity = idNote?.maxQuantity;

    const sourceValue = isPortatil ? kilograms : liters;
    const parsedValue = parseFloat(sourceValue) || 0;

    const shouldClamp = maxQuantity && parsedValue > maxQuantity;
    const effectiveValue = shouldClamp ? maxQuantity : parsedValue;

    const calculatedValue = isPortatil
      ? effectiveValue / density
      : effectiveValue * density;

    const formattedCalculated = parseFloat(
      Math.max(0, calculatedValue).toFixed(2),
    );
    const finalSource = shouldClamp ? maxQuantity : sourceValue;

    setValues({
      liters: isPortatil ? formattedCalculated : finalSource,
      kilograms: isPortatil ? finalSource : formattedCalculated,
    });
  }, [liters, kilograms, type, idNote]);

  useEffect(() => {
    const shouldFilter = idGasMovement?.label?.includes(TO_SKIP);

    setValues({
      notesFoliosOpt: responseNotes?.filter(
        (n) => n.type === (shouldFilter ? CREDIT : CASH),
      ),
      idNote: responseNotes?.length === 1 ? responseNotes[0] : null,
    });
  }, [idGasMovement, responseNotes]);

  return (
    <FormControl fullWidth className={animations.fadeIn}>
      <Grid
        container
        spacing={1}
        classes={{
          root: {
            ref: ref,
          },
        }}
      >
        <Grid item xs={12}>
          <Autocomplete
            id="gas-mov-request-autocomplete-idStation"
            options={stationsList}
            value={idStation}
            renderInput={(params) => (
              <TextField
                {...params}
                value={idStation}
                name="idStation"
                label="Estación"
                variant="outlined"
                error={fieldErrors.idStation}
                helperText={fieldErrors.idStation || ""}
              />
            )}
            fullWidth
            onChange={(_, v) => handleChange(v)}
            noOptionsText="No se encuentra la planta"
            disabled={isEdit}
            textFieldProps={{
              error: fieldErrors.idStation,
              helperText: fieldErrors.idStation,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Fecha de Movimiento"
            type="date"
            name="dateMovement"
            value={dateMovement}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            required={true}
            onChange={handleInputOnChange}
            inputProps={{
              min: dates().dateTempString,
              max: dates().maxDateString,
            }}
            fullWidth
            disabled={isEdit}
            error={errorInv}
            helperText={messageInv || ""}
          />
        </Grid>
        <Grid item xs={6}>
          <AutocompleteComponent
            label="Tipo de Unidad"
            name="type"
            value={type}
            options={typesUnitsOptMemo}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tipo de Unidad"
                variant="outlined"
              />
            )}
            required={true}
            fullWidth
            handleValues={handleChangeUnitType}
            disabled={isEdit || !idStation?.id}
            textFieldProps={{
              error: fieldErrors.type,
              helperText: fieldErrors.type,
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <AutocompleteComponent
            label="Unidad"
            options={unitsByTypeMemo}
            name="idUnit"
            value={idUnit}
            renderInput={(params) => (
              <TextField {...params} label="Unidad" variant="outlined" />
            )}
            required={true}
            fullWidth
            handleValues={handleChangeUnit}
            disabled={isEdit}
            textFieldProps={{
              error: fieldErrors.idUnit,
              helperText: fieldErrors.idUnit,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <AutocompleteComponent
            label="Tipo de Movimiento"
            options={movGasOpt}
            value={idGasMovement}
            name="idGasMovement"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tipo de Movimiento"
                variant="outlined"
              />
            )}
            required={true}
            fullWidth
            handleValues={handleChangeMov}
            textFieldProps={{
              error: fieldErrors.idGasMovement,
              helperText: fieldErrors.idGasMovement,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <AutocompleteComponent
            label="Seleccionar Nota"
            options={notesFoliosOpt}
            value={idNote}
            name="idNote"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Seleccionar Nota"
                variant="outlined"
              />
            )}
            required={idGasMovement?.withoutNote === 1 ? false : true}
            fullWidth
            handleValues={setValues}
            disabled={loadingNotes || isEdit}
            textFieldProps={{
              error: fieldErrors.idNote,
              helperText: fieldErrors.idNote,
            }}
          />
        </Grid>
        {isTransfer && (
          <>
            <Grid item xs={12} sm={6}>
              <AutocompleteComponent
                name="idStationDestino"
                label="Planta Destino"
                options={stationsList}
                value={inputValues.idStationDestino}
                handleValues={(vals) => setValues({ ...vals, idStorageDestino: null })}
                sort
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <AutocompleteComponent
                name="idStorageDestino"
                label="Almacén Destino"
                options={warehouseOptions}
                value={inputValues.idStorageDestino}
                handleValues={setValues}
                disabled={!inputValues.idStationDestino}
                sort
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Comentarios"
            name="comment"
            value={comment}
            onChange={handleInputOnChange}
            variant="outlined"
            multiline
            rows={4}
            error={fieldErrors.comment}
            helperText={fieldErrors.comment || ""}
            required={true}
            disabled={isEdit}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            type="number"
            fullWidth
            label={`Litros - Cantidad maxima permitida: ${type?.value?.includes(ESTACION) && idNote?.maxQuantity
              ? idNote?.maxQuantity
              : 0
              }`}
            name="liters"
            value={liters}
            onChange={handleInputOnChange}
            variant="outlined"
            error={fieldErrors.liters}
            helperText={
              (type?.value?.includes(ESTACION)
                ? `Limite ${idNote?.maxQuantity || 0} lts`
                : "") || fieldErrors.liters
            }
            disabled={(type && type?.value === PORTATIL) || !type || isEdit}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">Lts</InputAdornment>
              ),
              inputProps: { min: 0, max: idNote?.maxQuantity },
            }}
            onFocus={() => resetOnClick(handleInputOnChange, liters, "liters")}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label={`Kilogramos - Cantidad maxima permitida: ${type?.value?.includes(PORTATIL) && idNote?.maxQuantity
              ? idNote?.maxQuantity
              : 0
              }`}
            name="kilograms"
            disabled={(type && type?.value !== PORTATIL) || !type || isEdit}
            value={kilograms}
            onChange={handleInputOnChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">Kg</InputAdornment>
              ),
              inputProps: { min: 0, max: idNote?.maxQuantity },
            }}
            onFocus={() =>
              resetOnClick(handleInputOnChange, kilograms, "kilograms")
            }
            error={!!fieldErrors.kilograms}
            helperText={
              (type?.value === PORTATIL
                ? `Limite ${idNote?.maxQuantity || 0} kgs`
                : "") || fieldErrors.kilograms
            }
          />
        </Grid>
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <RegularButton
            color="danger"
            onClick={onClose}
            disabled={isLoadingComp?.GM_newLoading}
          >
            Cancelar
          </RegularButton>
          <RegularButton
            color="info"
            onClick={handleSubmit}
            disabled={!isSuccess}
            loading={isLoadingComp?.GM_newLoading}
          >
            Agregar
          </RegularButton>
        </Grid>
      </Grid>
    </FormControl>
  );
};
